import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, BrainCircuit, Wrench, GitGraph, ChevronLeft, ChevronRight, Search, Globe, Database, Webhook, Bot, Settings, LayoutDashboard, File as FileFlow, CreditCard, UserCog, Building2, LogOut } from 'lucide-react';
import { supabase } from '../lib/supabase';

const components = [
  { 
    type: 'agent', 
    icon: Users, 
    label: 'CrewAI Agent', 
    color: 'text-blue-400',
    description: 'Add an AI agent to your workflow'
  },
  { 
    type: 'task', 
    icon: BrainCircuit, 
    label: 'CrewAI Agent Task', 
    color: 'text-emerald-400',
    description: 'Define a task for agents to perform'
  },
  { 
    type: 'tool', 
    icon: Wrench, 
    label: 'CrewAI Task Tool', 
    color: 'text-purple-400',
    description: 'Add external tools and integrations'
  },
  { 
    type: 'flow', 
    icon: GitGraph, 
    label: 'CrewAI Flow', 
    color: 'text-orange-400',
    description: 'Control workflow execution'
  },
];

const integrations = [
  { icon: Globe, label: 'HTTP', color: 'text-blue-400' },
  { icon: Database, label: 'Database', color: 'text-green-400' },
  { icon: Webhook, label: 'Webhooks', color: 'text-red-400' },
  { icon: Bot, label: 'OpenAI', color: 'text-purple-400' },
];

const navigationLinks = [
  { icon: LayoutDashboard, label: 'Dashboard', to: '/dashboard', color: 'text-blue-400' },
  { icon: FileFlow, label: 'Workflows', to: '/workflows', color: 'text-emerald-400' },
  { icon: Building2, label: 'Projects', to: '/projects', color: 'text-purple-400' },
  { icon: CreditCard, label: 'Billing', to: '/billing', color: 'text-orange-400' },
  { icon: UserCog, label: 'Account', to: '/account', color: 'text-yellow-400' },
];

export function Sidebar({ 
  isCollapsed,
  onToggleCollapse
}: {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<'components' | 'integrations'>('components');
  const [showSettings, setShowSettings] = useState(false);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className={`relative flex flex-col h-full bg-gray-900 border-r border-gray-800 transition-all duration-300 ${
      isCollapsed ? 'w-20' : 'w-80'
    }`}>
      {/* Collapse Toggle */}
      <button
        onClick={onToggleCollapse}
        className="absolute -right-3 top-1/2 transform -translate-y-1/2 bg-gray-800 border border-gray-700 rounded-full p-1 hover:bg-gray-700 transition-colors z-10 sidebar-collapse-btn"
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-gray-800">
        <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
          <GitGraph className="w-6 h-6 text-white" />
        </div>
        {!isCollapsed && (
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-white truncate">Agentic Flow Editor</h1>
            <p className="text-sm text-gray-400">Build AI Agentic workflows</p>
          </div>
        )}
      </div>

      {/* Search */}
      {!isCollapsed && (
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search components..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>
      )}

      {/* Category Tabs */}
      {!isCollapsed && (
        <div className="px-4 mb-2">
          <div className="flex gap-2 p-1 bg-gray-800 rounded-lg">
            <button
              onClick={() => setSelectedCategory('components')}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                selectedCategory === 'components' 
                  ? 'bg-indigo-600 text-white' 
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Components
            </button>
            <button
              onClick={() => setSelectedCategory('integrations')}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                selectedCategory === 'integrations' 
                  ? 'bg-indigo-600 text-white' 
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              Integrations
            </button>
          </div>
        </div>
      )}

      {/* Components/Integrations List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {selectedCategory === 'components' ? (
          components
            .filter(c => 
              c.label.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((component) => {
              const Icon = component.icon;
              return (
                <div
                  key={component.type}
                  className={`flex items-center p-3 bg-gray-800 rounded-lg cursor-move hover:bg-gray-700 transition-colors border border-gray-700 hover:border-gray-600 group ${
                    isCollapsed ? 'justify-center' : ''
                  }`}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('application/reactflow', component.type);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                >
                  <Icon className={`w-5 h-5 ${component.color}`} />
                  {!isCollapsed && (
                    <div className="ml-3 flex-1">
                      <div className="text-gray-200">{component.label}</div>
                      <div className="text-xs text-gray-400">{component.description}</div>
                    </div>
                  )}
                </div>
              );
            })
        ) : (
          integrations.map((integration) => {
            const Icon = integration.icon;
            return (
              <div
                key={integration.label}
                className={`flex items-center p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors border border-gray-700 hover:border-gray-600 ${
                  isCollapsed ? 'justify-center' : ''
                }`}
              >
                <Icon className={`w-5 h-5 ${integration.color}`} />
                {!isCollapsed && (
                  <span className="ml-3 text-gray-300">{integration.label}</span>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Settings Section */}
      <div className="border-t border-gray-800 p-4">
        <div className="space-y-2">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`w-full flex items-center p-2 rounded-lg transition-colors ${
              showSettings ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-gray-300 hover:bg-gray-800'
            }`}
          >
            <Settings className="w-5 h-5" />
            {!isCollapsed && <span className="ml-3">Settings</span>}
          </button>
          
          {showSettings && !isCollapsed && (
            <div className="pl-2 space-y-1">
              {navigationLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center p-2 text-gray-400 hover:text-gray-300 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <link.icon className={`w-4 h-4 ${link.color}`} />
                  <span className="ml-3 text-sm">{link.label}</span>
                </Link>
              ))}
              <button
                onClick={handleSignOut}
                className="w-full flex items-center p-2 text-red-400 hover:text-red-300 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="ml-3 text-sm">Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
